import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react'
import { Input, Button, Alert } from '@components/common'
import PasswordStrengthIndicator from './PasswordStrengthIndicator'
import './ProfileSecurityTab.css'

/**
 * Password Change Validation Schema (Strong Policy)
 */
const passwordSchema = z
	.object({
		current_password: z.string().min(1, 'Current password is required'),
		new_password: z
			.string()
			.min(8, 'Password must be at least 8 characters')
			.regex(/[A-Z]/, 'Must contain at least one uppercase letter')
			.regex(/[a-z]/, 'Must contain at least one lowercase letter')
			.regex(/[0-9]/, 'Must contain at least one number')
			.regex(/[^A-Za-z0-9]/, 'Must contain at least one special character'),
		confirm_password: z.string().min(1, 'Please confirm your password'),
	})
	.refine((data) => data.new_password === data.confirm_password, {
		message: "Passwords don't match",
		path: ['confirm_password'],
	})
	.refine((data) => data.current_password !== data.new_password, {
		message: 'New password must be different from current password',
		path: ['new_password'],
	})

/**
 * Profile Security Tab Component
 *
 * Change password functionality with strength indicator.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onChangePassword - Password change handler
 * @param {boolean} props.saving - Saving state
 */
const ProfileSecurityTab = ({ onChangePassword, saving = false }) => {
	const [error, setError] = useState(null)
	const [success, setSuccess] = useState(false)
	const [showCurrentPassword, setShowCurrentPassword] = useState(false)
	const [showNewPassword, setShowNewPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		watch,
		reset,
	} = useForm({
		resolver: zodResolver(passwordSchema),
		mode: 'onChange',
		defaultValues: {
			current_password: '',
			new_password: '',
			confirm_password: '',
		},
	})

	const newPassword = watch('new_password')

	const onSubmit = async (data) => {
		try {
			setError(null)
			setSuccess(false)
			await onChangePassword(
				data.current_password,
				data.new_password,
				data.confirm_password
			)
			setSuccess(true)
			reset()
		} catch (err) {
			setError(err.message || 'Failed to change password')
		}
	}

	return (
		<div className="profile-security-tab">
			{/* Header */}
			<div className="profile-security-tab__header">
				<div className="profile-security-tab__icon-container">
					<Lock size={20} />
				</div>
				<div>
					<h3 className="profile-security-tab__title">Change Password</h3>
					<p className="profile-security-tab__subtitle">
						Update your password to keep your account secure
					</p>
				</div>
			</div>

			{/* Security Warning */}
			<div className="profile-security-tab__warning">
				<AlertTriangle size={16} />
				<span>
					After changing your password, you may need to sign in again on other devices.
				</span>
			</div>

			{/* Alerts */}
			{error && (
				<Alert
					variant="error"
					message={error}
					dismissible
					onClose={() => setError(null)}
				/>
			)}

			{success && (
				<Alert
					variant="success"
					message="Password changed successfully!"
					dismissible
					onClose={() => setSuccess(false)}
				/>
			)}

			{/* Form */}
			<form onSubmit={handleSubmit(onSubmit)} className="profile-security-tab__form">
				{/* Current Password */}
				<div className="profile-security-tab__field">
					<label className="profile-security-tab__label">Current Password</label>
					<div className="profile-security-tab__input-wrapper">
						<Input
							{...register('current_password')}
							type={showCurrentPassword ? 'text' : 'password'}
							error={errors.current_password?.message}
							disabled={saving}
							placeholder="Enter your current password"
							autoComplete="current-password"
						/>
						<button
							type="button"
							className="profile-security-tab__toggle-btn"
							onClick={() => setShowCurrentPassword(!showCurrentPassword)}
							tabIndex={-1}
						>
							{showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
				</div>

				{/* New Password */}
				<div className="profile-security-tab__field">
					<label className="profile-security-tab__label">New Password</label>
					<div className="profile-security-tab__input-wrapper">
						<Input
							{...register('new_password')}
							type={showNewPassword ? 'text' : 'password'}
							error={errors.new_password?.message}
							disabled={saving}
							placeholder="Enter new password"
							autoComplete="new-password"
						/>
						<button
							type="button"
							className="profile-security-tab__toggle-btn"
							onClick={() => setShowNewPassword(!showNewPassword)}
							tabIndex={-1}
						>
							{showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>

					{/* Password Strength Indicator */}
					{newPassword && <PasswordStrengthIndicator password={newPassword} />}
				</div>

				{/* Confirm Password */}
				<div className="profile-security-tab__field">
					<label className="profile-security-tab__label">Confirm New Password</label>
					<div className="profile-security-tab__input-wrapper">
						<Input
							{...register('confirm_password')}
							type={showConfirmPassword ? 'text' : 'password'}
							error={errors.confirm_password?.message}
							disabled={saving}
							placeholder="Confirm new password"
							autoComplete="new-password"
						/>
						<button
							type="button"
							className="profile-security-tab__toggle-btn"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
							tabIndex={-1}
						>
							{showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
						</button>
					</div>
				</div>

				{/* Submit Button */}
				<div className="profile-security-tab__actions">
					<Button
						type="submit"
						variant="primary"
						loading={saving}
						disabled={!isValid}
					>
						<Lock size={16} />
						<span>Change Password</span>
					</Button>
				</div>
			</form>
		</div>
	)
}

ProfileSecurityTab.propTypes = {
	onChangePassword: PropTypes.func.isRequired,
	saving: PropTypes.bool,
}

export default ProfileSecurityTab
